import { useQuery } from "@tanstack/react-query";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { numberifyDecimalFields } from "./use-deals-hooks/decimal-fields";

/** Deal list item shape returned by paginated queries (subset of full Deal) */
export interface DealListItem {
  id: string;
  title: string;
  description?: string;
  company_name?: string;
  company_id?: string;
  stage?: string;
  revenue?: number;
  tags?: string[];
  assigned_users?: string[];
  engineer_ids?: string[];
  estimated_hours?: number;
}

export interface UseDealsPaginatedParams {
  page?: number;
  limit?: number;
  search?: string;
}

/** Row shape returned by the `search_deals` RPC (superset of DealListItem). */
interface DealSearchRow extends DealListItem {
  total_count?: number;
}

export function useDealsPaginated(params?: UseDealsPaginatedParams) {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 30;
  const search = params?.search?.trim();

  return useQuery({
    queryKey: ["deals-paginated", page, limit, search],
    queryFn: async () => {
      const supabase = getBrowserSupabaseClient();

      if (search && search.length > 1) {
        const { data, error } = await supabase.rpc("search_deals", {
          p_query: search,
          p_limit: limit,
          p_offset: (page - 1) * limit,
        });
        if (error) throw error;
        const rows = (data || []) as DealSearchRow[];
        return {
          data: rows.map((r) => ({
            id: r.id,
            title: r.title,
            description: r.description,
            company_name: r.company_name,
            company_id: r.company_id,
            stage: r.stage,
            revenue: r.revenue,
            tags: r.tags,
            assigned_users: r.assigned_users,
            engineer_ids: r.engineer_ids,
            estimated_hours: r.estimated_hours,
          })).map(numberifyDecimalFields),
          count: rows[0]?.total_count ?? rows.length,
          page,
        };
      }

      const from = (page - 1) * limit;
      const to = from + limit - 1;

      const { data, error, count } = await supabase
        .from("deals")
        .select("id, title, description, company_name, company_id, stage, revenue, tags, assigned_users, engineer_ids, estimated_hours", {
          count: "exact",
        })
        .order("last_activity_at", { ascending: false, nullsFirst: false })
        .range(from, to);

      if (error) throw error;
      return {
        data: ((data || []) as DealListItem[]).map(numberifyDecimalFields),
        count: count ?? 0,
        page,
      };
    },
    staleTime: 60 * 1000,
  });
}
